import {
  InputHTMLAttributes,
  SelectHTMLAttributes,
  useEffect,
  useState,
} from "react";
import {
  getRajEntity,
  getRajTrader,
  upadteRajTransaction,
} from "../../../Rajasthan/Utility/RestApi";
import Loading from "../../../../components/Loading";

interface InputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  name: string;
  type?: string;
  id?: string;
  placeholder?: string;
  required?: boolean;
  className?: string;
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  name: string;
  id?: string;
  options: { value: string | number; label: string }[];
  placeholder?: string;
  required?: boolean;
  className?: string;
  showPlaceholder?: boolean;
}

const SelectField: React.FC<SelectFieldProps> = ({
  label,
  name,
  id,
  options,
  placeholder = "Select...",
  required = false,
  className = "",
  defaultValue,
  showPlaceholder = true,
  ...rest
}) => {
  const stringDefaultValue =
    defaultValue !== undefined ? String(defaultValue) : undefined;

  useEffect(() => {
    if (defaultValue !== undefined) {
      console.log(`SelectField ${name} defaultValue:`, defaultValue);
      console.log(`Options:`, options);
      const matchingOption = options.find(
        (opt) => String(opt.value) === String(defaultValue)
      );
      console.log(`Matching option:`, matchingOption);
    }
  }, [defaultValue, options, name]);

  return (
    <>
      <label
        htmlFor={name}
        className="block text-sm font-medium text-gray-500 mb-1"
      >
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <select
          id={name}
          name={name}
          className={`appearance-none border p-2 mt-1 h-[39px] text-sm rounded-lg text-[#9CA3AF] w-full pr-10 ${className}`}
          required={required}
          {...rest}
        >
          {showPlaceholder && (
            <option className="text-[#9CA3AF]" value="">
              {placeholder}
            </option>
          )}
          {options.map((option) => {
            console.log("🚀 ~ {options.map ~ option:", option.value);
            return (
              <option
                key={option.value}
                value={option.value}
                selected={stringDefaultValue === String(option.value)}
              >
                {option.label}
              </option>
            );
          })}
        </select>
        {/* Custom arrow icon */}
        <div className="pointer-events-none absolute inset-y-0 right-1 flex items-center px-2 mt-1 text-[#9CA3AF]">
          <svg
            className="fill-current h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>
    </>
  );
};

const InputField: React.FC<InputFieldProps> = ({
  label,
  name,
  type = "text",
  id,
  placeholder,
  required = false,
  className = "",
  ...rest
}) => {
  const isDateOrTimeInput = type === "date" || type === "time";

  const inputProps = {
    type,
    id: id || name,
    name,
    className: `border p-2 mt-1 rounded-lg text-[#9CA3AF] w-full h-[39px] ${
      isDateOrTimeInput ? "custom-date-time-input" : ""
    } ${className}`,
    placeholder: type === "time" ? "HH:MM" : placeholder,
    required,

    ...(type === "time"
      ? {
          step: "1",
        }
      : { step: "any" }),
    ...rest,
  };

  return (
    <>
      <label
        htmlFor={id || name}
        className="block text-sm font-medium text-gray-500 mb-1"
      >
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div
        className={`relative ${
          isDateOrTimeInput ? "date-time-input-wrapper" : ""
        }`}
      >
        <input {...inputProps} />
      </div>
      <style>{`
  .custom-date-time-input::-webkit-calendar-picker-indicator {
    filter: invert(72%) sepia(9%) saturate(261%) hue-rotate(184deg) brightness(93%) contrast(85%);
    opacity: 1;
  }

  /* Ensure the input text doesn't overlap with our custom icon */
  .custom-date-time-input {
    padding-right: 10px;
  }
  
  /* Force 24-hour format in supported browsers */
  input[type="time"] {
    -moz-appearance: textfield;
    -webkit-appearance: textfield;
    appearance: textfield;
  }
`}</style>
    </>
  );
};

interface EditFormProps {
  setDataTab: (data: any) => void;
}

const EditForm: React.FC<EditFormProps> = ({ setDataTab }) => {
  const [entity, setEntity] = useState<{ id: number; name: string }[]>([]);
  const [trader, setTrader] = useState<{ name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingFull, setLoadingFull] = useState(false);
  const [data, setData] = useState<any>({});

  async function handleFetch() {
    setLoadingFull(true);
    const entity = await getRajEntity();
    const trader = await getRajTrader();
    setEntity(entity);
    setTrader(trader);
    setLoadingFull(false);
  }

  useEffect(() => {
    handleFetch();
  }, []);

  useEffect(() => {
    const aa = localStorage.getItem("editContractData");
    if (!aa) {
      setDataTab("form");
      return;
    }
    setData(JSON.parse(aa));
  }, [setDataTab]);

  const handleSubmit = async (e: any) => {
    setLoading(true);
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Start with an object that includes just the ID
    let formData2: { [key: string]: string | number } = {
      id: data.id, // Always include the ID
    };

    // Track changes
    let hasChanges = false;

    for (const [key, value] of formData.entries()) {
      const inputElement = form.elements.namedItem(key) as HTMLInputElement;
      let formattedValue: string | number;

      // Format the value based on input type
      if (inputElement && inputElement.type === "number") {
        formattedValue = value === "" ? "" : parseFloat(value as string);
      } else if (value instanceof File) {
        formattedValue = value.name;
      } else {
        formattedValue = value as string;
      }

      // Compare with original data - only include if different
      // Handle both string and number comparisons
      const originalValue = data[key];
      const originalValueStr =
        originalValue !== undefined ? String(originalValue) : "";
      const formattedValueStr =
        formattedValue !== undefined ? String(formattedValue) : "";

      if (formattedValueStr !== originalValueStr) {
        formData2[key] = formattedValue;
        hasChanges = true;
        console.log(
          `Field ${key} changed from "${originalValue}" to "${formattedValue}"`
        );
      }
    }

    if (!hasChanges) {
      alert("No changes detected");
      setLoading(false);
      return;
    }

    try {
      console.log("Submitting changed fields only:", formData2); // Log what's being sent
      const aa = await upadteRajTransaction(Number(formData2.id), formData2);
      if (aa.message == "Transaction updated successfully") {
        alert("Transaction updated successfully");
        // After successful update, go back to table view
        setDataTab({ label: "Contract", value: "table" });
        // Clear localStorage
        localStorage.removeItem("editContractData");
      }
    } catch (error) {
      console.log(error);
      alert("Error in updating the form");
    }

    setLoading(false);
  };
  return (
    <>
      <>{loadingFull && <Loading />}</>
      <form onSubmit={handleSubmit} className="">
        <div className="grid grid-cols-4 gap-6 px-4">
          <div className="">
            <InputField
              label="Start Date"
              name="start_date"
              placeholder="Enter Start Date"
              required
              type="date"
              defaultValue={data.start_date}
            />
          </div>
          <div className="">
            <InputField
              label="End Date"
              name="end_date"
              placeholder="Enter End Date"
              required
              type="date"
              defaultValue={data.end_date}
            />
          </div>
          <div className="">
            <InputField
              label="Withdrawal Loss (%)"
              name="withdrawal_loss"
              placeholder="Enter Withdrawal Loss"
              type="number"
              defaultValue={data.withdrawal_loss}
            />
          </div>
          <div className="">
            <InputField
              label="LOI Number"
              name="loi_number"
              placeholder="Enter LOI Number"
              type="number"
              defaultValue={data.loi_number}
            />
          </div>
          <div className="">
            <InputField
              label="LOI Date"
              name="loi_date"
              placeholder="Enter LOI Date"
              type="date"
              defaultValue={data.loi_date}
            />
          </div>

          <div className="">
            <SelectField
              label="Schedule In"
              name="schedule_in"
              options={[
                { value: "GNA", label: "GNA" },
                { value: "TGNA", label: "TGNA" },
              ]}
              placeholder="Select Contract Type"
              required
              defaultValue={data.schedule_in}
              showPlaceholder={false}
            />
          </div>
          <div className="">
            <SelectField
              label="Type of Transaction"
              name="type_of_transaction"
              options={[
                { value: "LTA", label: "LTA" },
                { value: "UA", label: "UA" },
                { value: "DEEP TN-27", label: "DEEP TN-27" },
                { value: "LDC-RA", label: "LDC-RA" },
                { value: "INTRA STATE", label: "INTRA STATE" },
              ]}
              placeholder="Select Type of Transaction"
              defaultValue={data.type_of_transaction}
            />
          </div>
          <div className="">
            <InputField
              label="GNA Number"
              name="gna_number"
              placeholder="Enter GNA Number"
              type="number"
              defaultValue={data.gna_number}
            />
          </div>
          <div className="">
            <SelectField
              label="Trader"
              name="trader"
              options={trader.map((e: any) => ({
                value: e.name,
                label: e.name,
              }))}
              placeholder="Select Trader"
              required
              defaultValue={data.trader}
            />
          </div>
          <div className="">
            <SelectField
              label="Injecting Entity"
              name="entity"
              options={entity.map((e: any) => ({
                value: e.id,
                label: e.name,
              }))}
              placeholder="Select Injecting Entity"
              required
              defaultValue={(() => {
                const selectedEntity = entity.find(
                  (e) => e.name === data.entity
                );

                return selectedEntity ? selectedEntity.id : "";
              })()}
            />
          </div>
        </div>

        <div className="my-8 bg-[#FDFDFD] border border-[#f1f1f1] p-5 rounded-xl">
          <h2 className="text-[16px] text-[#8C8A96] font-medium ">
            GNA Contract Details :
          </h2>

          <div className="grid grid-cols-4 gap-10 mt-4">
            <div className="">
              <InputField
                label="Contract Date From"
                name="gna_contract_date_from"
                placeholder="Enter Contract Date From"
                type="date"
                defaultValue={data.gna_contract_date_from}
              />
            </div>
            <div className="">
              <InputField
                label="Contract Date To"
                name="gna_contract_date_to"
                placeholder="Enter Contract Date To"
                type="date"
                defaultValue={data.gna_contract_date_to}
              />
            </div>
            <div className="">
              <InputField
                label="Contract Time From"
                name="gna_contract_time_from"
                placeholder="Enter Contract Time From"
                type="time"
                defaultValue={data.gna_contract_time_from}
              />
            </div>
            <div className="">
              <InputField
                label="Contract Time To"
                name="gna_contract_time_to"
                placeholder="Enter Contract Time To"
                type="time"
                defaultValue={data.gna_contract_time_to}
              />
            </div>
          </div>
        </div>

        <div className="p-5 pt-0">
          <h2 className="text-[16px] text-[#8C8A96] font-medium ">
            PPA Contract Details :
          </h2>

          <div className="grid grid-cols-4 gap-10 mt-4">
            <div className="">
              <InputField
                label="Contract Capacity (mw)"
                name="ppa_contract_capacity"
                placeholder="Enter Contract Capacity"
                required
                type="number"
                defaultValue={data.ppa_contract_capacity}
              />
            </div>
            <div className="">
              <InputField
                label="Rate"
                name="rate"
                placeholder="Enter Rate"
                type="number"
                defaultValue={data.rate}
              />
            </div>
            <div className="">
              <InputField
                label="GNA Contract Created (mw)"
                name="gna_contract_created"
                placeholder="Enter GNA Contract Created"
                type="number"
                defaultValue={data.gna_contract_created}
              />
            </div>
            <div className="">
              <InputField
                label="Ruvitl Scheduling Request"
                name="ruvitl_scheduling_request"
                placeholder="Enter Ruvitl Scheduling Request"
                required
                type="number"
                defaultValue={data.ruvitl_scheduling_request}
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-center my-3">
          <button
            disabled={loading}
            type="submit"
            className="px-8 py-3 bg-primary text-white rounded-xl font-medium text-[0.8rem]"
          >
            {loading ? "Submitting..." : "Update"}
          </button>
        </div>
      </form>
    </>
  );
};

export default EditForm;
